/**
 * "Add a pool account" modal (rev 7 UI simplification — 21/7, from a live prod screenshot review).
 * Replaces the old ALWAYS-VISIBLE inline two-tab section on Pool Settings with a single "Add Account"
 * button that opens this modal:
 *
 *  step "choose" — two option cards: an existing ZPCD-mirrored sub-account, or a standalone Zoom S2S
 *    credential (the case a mirror search can't reach — 5003399647, 86 paying customers, never landed
 *    in the mirror; see PoolSettingsPage's own banner).
 *  step "subaccount" / "credential" — the SAME search-by-name/number panel and the SAME credential form
 *    the old inline tabs used, just relocated here, with a Back control (in the footer) returning to
 *    "choose". Each step keeps its OWN original error-surfacing shape (a shared inline alert for the
 *    credential form, a per-attempt inline alert for the search-and-add table) — moved from a toast to
 *    an inline alert for the search path specifically, because a toast render BEHIND this native
 *    <dialog>'s top-layer backdrop while the modal is still open (the old inline section never had that
 *    problem — it was never itself under a backdrop).
 *
 * On a successful add (either path) the modal closes and the accounts list refreshes — both mutations
 * already invalidate `poolKeys.accounts` (hooks.ts) — with the same success toast as before (that one
 * still fires AFTER the modal is gone, so there's no backdrop to hide behind).
 *
 * Focus on phase change (QA finding, fixed 21/7): the native <dialog>'s own showModal() autofocus only
 * fires ONCE, on the very first open — switching `phase` inside the SAME still-open dialog does not, on
 * its own, move focus anywhere (a keyboard/screen-reader user is left on the Back button even after it
 * re-renders, or on nothing at all if the previously-focused element is gone from the DOM). `firstFieldRef`
 * + the `useEffect` keyed on `phase` below fixes that: each step attaches the ref to ITS OWN first
 * interactive control (the first option card / the search input / the account-name field — see each
 * step's own comment for exactly which, and CredentialStep's for the one deliberate DOM-order exception).
 */
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Check, Copy, KeyRound, Search } from "lucide-react";
import { ApiRequestError } from "@/api/client";
import {
  Button,
  EmptyState,
  IconButton,
  Input,
  Modal,
  SkeletonRows,
  StatusPill,
  Table,
  useNotifier,
  type Column,
} from "@/components/ui";
import { PoolErrorState } from "@/features/pool/PoolErrorState";
import {
  MIN_CANDIDATE_CHARS,
  useAddPoolAccount,
  useAddPoolAccountFromCredential,
  usePoolCandidates,
} from "@/features/pool/hooks";
import type { PoolAccountCandidate, PoolAddCredentialBody } from "@/api/types";

function errMessage(e: unknown, fallback: string): string {
  return e instanceof ApiRequestError ? e.error.message : fallback;
}

/** account_id is the only field guaranteed non-null — same fallback chain as PoolSettingsPage's own
 *  accountLabel(); duplicated locally rather than imported, matching this module's own convention of a
 *  tiny local errMessage() per modal file (see PeriodModal.tsx / ForceActionDialog.tsx). */
function accountLabel(a: {
  account_name: string | null;
  account_number: string | null;
  account_id: string;
}): string {
  return a.account_name ?? a.account_number ?? a.account_id;
}

/** The exact granular scopes an operator must tick when creating the Server-to-Server OAuth app in the
 *  standalone account — literal Zoom scope tokens, not translatable prose (same treatment as the raw
 *  `action` strings ForceActionDialog renders verbatim from the server). Keep this list and
 *  service.py's `add_credential_account` verify-scope hint in the API repo in sync by hand — there is no
 *  shared source of truth across the two repos. */
// rev 9 (21/7, principal decision — managed-domain removal): account:read:managed_domains:admin is
// GONE from this list — the guardrail it fed no longer exists anywhere in the module (see
// PeriodModal.tsx's own banner). 2 read + 2 write, group labels unchanged.
const REQUIRED_SCOPES = [
  // อ่านข้อมูล — sweep ใช้ตั้งแต่วันแรก
  { scope: "user:read:list_users:admin", group: "read" },
  { scope: "user:read:user:admin", group: "read" },
  // ถอด license — โค้ด write ฝั่ง standalone ยังไม่เปิดใช้ แต่คุณใหม่สั่งให้ทีมติ๊กครบตั้งแต่
  // สร้าง app (21/7) จะได้ไม่ต้องกลับมาแก้ตอนเปิดระบบถอดจริง (S2S เพิ่ม scope ทีหลังได้ก็จริง
  // แต่เลือกความสะดวกของทีมเป็นหลัก)
  { scope: "user:update:user:admin", group: "write" },
  { scope: "user:delete:user:admin", group: "write" },
] as const;

type Phase = "choose" | "subaccount" | "credential";

export function AddAccountModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<Phase>("choose");

  // See this file's own banner. One ref, reattached by whichever step is mounted — a callback ref
  // (rather than handing each step the SAME RefObject typed to a common element) so TS keeps each step's
  // own attach site correctly typed to its own element (HTMLButtonElement / HTMLInputElement).
  const firstFieldRef = useRef<HTMLElement | null>(null);
  const setFirstField = useCallback((el: HTMLElement | null) => {
    firstFieldRef.current = el;
  }, []);
  useEffect(() => {
    firstFieldRef.current?.focus();
  }, [phase]);

  const title =
    phase === "choose"
      ? t("pool.settings.addAccountTitle")
      : phase === "subaccount"
        ? t("pool.settings.modeSubAccount")
        : t("pool.settings.modeCredential");

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      // ขั้น choose เป็นการ์ดเลือก 2 ใบ = md พอ · ขั้นค้นหา/ฟอร์ม cred มีตาราง 5 คอลัมน์+ปุ่มต่อแถว
      // ต้อง lg ไม่งั้นชื่อ account ตัดสามบรรทัดและคอลัมน์ขวาตกขอบ (คุณใหม่เจอบน prod 21/7)
      size={phase === "choose" ? "md" : "lg"}
      footer={
        phase === "choose" ? undefined : (
          <Button variant="secondary" onClick={() => setPhase("choose")}>
            {t("action.back")}
          </Button>
        )
      }
    >
      {phase === "choose" && (
        <ChooseTypeStep onChoose={(mode) => setPhase(mode)} firstFieldRef={setFirstField} />
      )}
      {phase === "subaccount" && (
        <ExistingAccountStep onAdded={onClose} firstFieldRef={setFirstField} />
      )}
      {phase === "credential" && <CredentialStep onAdded={onClose} firstFieldRef={setFirstField} />}
    </Modal>
  );
}

// --- Step 1: choose type -------------------------------------------------------------------------------

function ChooseTypeStep({
  onChoose,
  firstFieldRef,
}: {
  onChoose: (mode: Exclude<Phase, "choose">) => void;
  firstFieldRef: (el: HTMLElement | null) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-4">
      <p className="text-(length:--text-sm) text-(--text-muted)">
        {t("pool.settings.chooseTypePrompt")}
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <OptionCard
          icon={<Search className="size-5" aria-hidden />}
          title={t("pool.settings.modeSubAccount")}
          description={t("pool.settings.modeSubAccountDesc")}
          onClick={() => onChoose("subaccount")}
          buttonRef={firstFieldRef}
        />
        <OptionCard
          icon={<KeyRound className="size-5" aria-hidden />}
          title={t("pool.settings.modeCredential")}
          description={t("pool.settings.modeCredentialDesc")}
          onClick={() => onChoose("credential")}
        />
      </div>
    </div>
  );
}

function OptionCard({
  icon,
  title,
  description,
  onClick,
  buttonRef,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  onClick: () => void;
  /** Only the card that should receive focus on this phase's mount passes one (see ChooseTypeStep). */
  buttonRef?: (el: HTMLButtonElement | null) => void;
}) {
  // aria-labelledby/aria-describedby (rather than letting the accessible name fall out of ALL of the
  // button's text content) so a screen reader announces the NAME as just "From existing account", with
  // the "how" sentence read as a separate description — and so this stays queryable by its short title
  // alone (`getByRole("button", { name: "From existing account" })`), not the whole card's prose.
  const titleId = useId();
  const descId = useId();
  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={onClick}
      aria-labelledby={titleId}
      aria-describedby={descId}
      className="flex flex-col items-start gap-2 rounded-(--radius) border border-(--border) bg-(--surface) p-4 text-left shadow-(--shadow-sm) transition-colors duration-(--dur-fast) ease-(--ease-out) hover:border-(--brand) hover:bg-(--surface-hover) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--brand)"
    >
      <span className="flex size-9 items-center justify-center rounded-(--radius-sm) bg-(--surface-sunken) text-(--brand)">
        {icon}
      </span>
      <span id={titleId} className="text-(length:--text-sm) font-semibold text-(--text-strong)">
        {title}
      </span>
      <span id={descId} className="text-(length:--text-xs) text-(--text-muted)">
        {description}
      </span>
    </button>
  );
}

// --- Step 2a: existing account (the old "From existing account" tab, relocated) ------------------------

function ExistingAccountStep({
  onAdded,
  firstFieldRef,
}: {
  onAdded: () => void;
  firstFieldRef: (el: HTMLElement | null) => void;
}) {
  const { t } = useTranslation();
  const { notify } = useNotifier();
  const [term, setTerm] = useState("");
  const candidatesQ = usePoolCandidates(term);
  const addMut = useAddPoolAccount();
  const [error, setError] = useState<string | null>(null);

  const columns: Column<PoolAccountCandidate>[] = [
    {
      key: "name",
      header: t("pool.settings.colAccountName"),
      sortValue: (r) => r.account_name ?? "",
      cell: (r) => <span className="font-medium text-(--text)">{r.account_name ?? "—"}</span>,
    },
    {
      key: "number",
      header: t("pool.settings.colAccountNumber"),
      sortValue: (r) => r.account_number ?? "",
      cell: (r) => r.account_number ?? "—",
    },
    {
      key: "type",
      header: t("pool.settings.colAccountType"),
      sortValue: (r) => r.account_type ?? "",
      cell: (r) => r.account_type ?? "—",
    },
    {
      key: "seats",
      header: t("pool.settings.colSeats"),
      numeric: true,
      sortValue: (r) => r.seats ?? -1,
      cell: (r) => r.seats ?? "—",
    },
    {
      key: "actions",
      header: t("pool.settings.colActions"),
      align: "right",
      cell: (r) =>
        r.already_pool ? (
          <StatusPill tone="neutral">{t("pool.settings.alreadyPool")}</StatusPill>
        ) : (
          <Button
            size="sm"
            variant="secondary"
            loading={addMut.isPending && addMut.variables === r.account_id}
            onClick={() => {
              setError(null);
              addMut.mutate(r.account_id, {
                onSuccess: () => {
                  notify({
                    tone: "ok",
                    title: t("pool.settings.addSuccess", { account: accountLabel(r) }),
                  });
                  onAdded();
                },
                onError: (e) => {
                  const api = e instanceof ApiRequestError ? e.error : null;
                  setError(
                    api?.status === 409
                      ? t("pool.settings.addConflict")
                      : api?.status === 404
                        ? t("pool.settings.addNotFound")
                        : errMessage(e, t("state.errorTitle")),
                  );
                },
              });
            }}
          >
            {t("pool.settings.addButton")}
          </Button>
        ),
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <div className="relative w-full max-w-sm">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-(--text-subtle)"
          aria-hidden
        />
        <Input
          ref={firstFieldRef}
          type="search"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder={t("pool.settings.searchPlaceholder")}
          aria-label={t("pool.settings.searchLabel")}
          autoComplete="off"
          spellCheck={false}
          className="h-9 w-full pl-9"
        />
      </div>

      {/* Rendered inline rather than as a toast — a toast would draw behind this modal's own top-layer
          backdrop while the dialog is still open. */}
      {error && (
        <p role="alert" className="text-(length:--text-sm) text-(--danger)">
          {error}
        </p>
      )}

      {term.trim().length < MIN_CANDIDATE_CHARS ? (
        <p className="text-(length:--text-xs) text-(--text-subtle)">
          {t("pool.settings.searchHint")}
        </p>
      ) : candidatesQ.isPending ? (
        <SkeletonRows rows={3} />
      ) : candidatesQ.isError ? (
        <PoolErrorState error={candidatesQ.error} onRetry={() => void candidatesQ.refetch()} />
      ) : (
        <Table
          columns={columns}
          rows={candidatesQ.data.accounts}
          rowKey={(r) => r.account_id}
          caption={t("pool.settings.searchTitle")}
          paginated={false}
          empty={
            <EmptyState
              title={t("pool.settings.candidatesEmptyTitle")}
              body={t("pool.settings.candidatesEmptyBody")}
            />
          }
        />
      )}
    </div>
  );
}

// --- Step 2b: Zoom credential (the old "From Zoom credentials" tab, relocated) ---------------------------

function CredentialStep({
  onAdded,
  firstFieldRef,
}: {
  onAdded: () => void;
  firstFieldRef: (el: HTMLElement | null) => void;
}) {
  const { t } = useTranslation();
  const { notify } = useNotifier();
  const addMut = useAddPoolAccountFromCredential();

  const [accountName, setAccountName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [zoomAccountId, setZoomAccountId] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [copiedScope, setCopiedScope] = useState<string | null>(null);
  // Holds the "copied" reset timer so unmount can cancel it — otherwise a copy right before the modal
  // closes leaves a dangling 1.5s timeout that fires setCopiedScope into a torn-down tree (in tests:
  // "ReferenceError: window is not defined", caught after the environment was already swept).
  //
  // ONE DELIBERATE BEHAVIOUR CHANGE, and it is the better one: copying the SAME scope twice inside
  // 1.5s now RESTARTS the confirmation window, because the pending timer is cleared before the next is
  // armed. It used to leave the first timer running, so a second click could make "Copied" disappear
  // sooner than the first click's own window.
  const copiedResetTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(
    () => () => {
      if (copiedResetTimer.current) clearTimeout(copiedResetTimer.current);
    },
    [],
  );

  const canSubmit = Boolean(
    accountName.trim() &&
    accountNumber.trim() &&
    zoomAccountId.trim() &&
    clientId.trim() &&
    clientSecret,
  );

  async function copyScope(scope: string) {
    try {
      await navigator.clipboard.writeText(scope);
      setCopiedScope(scope);
      if (copiedResetTimer.current) clearTimeout(copiedResetTimer.current);
      copiedResetTimer.current = setTimeout(() => {
        copiedResetTimer.current = undefined;
        setCopiedScope((cur) => (cur === scope ? null : cur));
      }, 1500);
    } catch {
      // Clipboard API can be unavailable (older browser, non-secure context) — the scope is still
      // selectable/copyable by hand from the code line itself, so this is a silent no-op.
    }
  }

  function submit() {
    if (!canSubmit) return;
    setError(null);
    const body: PoolAddCredentialBody = {
      account_name: accountName.trim(),
      account_number: accountNumber.trim(),
      zoom_account_id: zoomAccountId.trim(),
      client_id: clientId.trim(),
      client_secret: clientSecret,
    };
    addMut.mutate(body, {
      onSuccess: () => {
        notify({
          tone: "ok",
          title: t("pool.settings.credSuccess", { account: body.account_name }),
        });
        onAdded();
      },
      // 503 (no POOL_CRED_KEY) / 422 (live verify against Zoom failed) / 409 (already a pool) — every
      // one rendered VERBATIM, same as every other refusal in this module.
      onError: (e) => setError(errMessage(e, t("pool.settings.credError"))),
    });
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 rounded-(--radius) border border-(--border) bg-(--surface-sunken) p-3">
        <p className="flex items-start gap-1.5 text-(length:--text-xs) text-(--text-muted)">
          <KeyRound className="mt-0.5 size-4 shrink-0 text-(--text-subtle)" aria-hidden />
          {t("pool.settings.credHelperText")}
        </p>
        <div className="flex flex-col gap-1.5">
          <span className="text-(length:--text-2xs) font-semibold uppercase tracking-wide text-(--text-subtle)">
            {t("pool.settings.credScopesTitle")}
          </span>
          <ul className="flex flex-col gap-1">
            {REQUIRED_SCOPES.map(({ scope, group }) => (
              <li
                key={scope}
                className="flex items-center justify-between gap-2 rounded-(--radius-sm) border border-(--border-hairline) bg-(--surface) px-2 py-1.5"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <code className="truncate text-(length:--text-xs) text-(--text)">{scope}</code>
                  <span className="shrink-0 text-(length:--text-2xs) text-(--text-subtle)">
                    {t(
                      group === "read"
                        ? "pool.settings.credScopeGroupRead"
                        : "pool.settings.credScopeGroupWrite",
                    )}
                  </span>
                </span>
                <IconButton
                  label={t("pool.settings.credScopeCopyLabel", { scope })}
                  icon={
                    copiedScope === scope ? (
                      <Check className="size-3.5" aria-hidden />
                    ) : (
                      <Copy className="size-3.5" aria-hidden />
                    )
                  }
                  size="sm"
                  onClick={() => void copyScope(scope)}
                />
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1.5">
          <span className="text-(length:--text-xs) font-medium text-(--text-muted)">
            {t("pool.settings.credAccountNameLabel")}
          </span>
          {/* The deliberate DOM-order exception (see this file's own banner): the scopes helper box
              above has its OWN first-focusable controls (the Copy icon buttons), but those are decorative
              reference material, not the step's actual task — this field is where filling out the form
              genuinely starts, so it gets the phase-change focus instead of whichever Copy button happens
              to sit first in the markup. */}
          <Input
            ref={firstFieldRef}
            value={accountName}
            onChange={(e) => setAccountName(e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-(length:--text-xs) font-medium text-(--text-muted)">
            {t("pool.settings.credAccountNumberLabel")}
          </span>
          <Input value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-(length:--text-xs) font-medium text-(--text-muted)">
            {t("pool.settings.credZoomAccountIdLabel")}
          </span>
          <Input
            value={zoomAccountId}
            onChange={(e) => setZoomAccountId(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-(length:--text-xs) font-medium text-(--text-muted)">
            {t("pool.settings.credClientIdLabel")}
          </span>
          <Input
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
        </label>
        <div className="flex flex-col gap-1.5 sm:col-span-2">
          <label className="flex flex-col gap-1.5">
            <span className="text-(length:--text-xs) font-medium text-(--text-muted)">
              {t("pool.settings.credClientSecretLabel")}
            </span>
            {/* type="password" + never echoed back by any endpoint, including this one's own success
                response (see PoolAddCredentialBody's banner) — the redaction lesson
                (feedback_redaction_is_a_leak_vector): this value never appears on screen again after Add
                succeeds. The helper note below is a SIBLING, not nested in the label, so it never
                pollutes the input's accessible name (a wrapper label's computed name includes every text
                node inside it). */}
            <Input
              type="password"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              autoComplete="new-password"
              spellCheck={false}
            />
          </label>
          <span className="text-(length:--text-2xs) text-(--text-subtle)">
            {t("pool.settings.credSecretNote")}
          </span>
        </div>
      </div>

      {error && (
        <p role="alert" className="text-(length:--text-sm) text-(--danger)">
          {error}
        </p>
      )}

      <Button
        loading={addMut.isPending}
        disabled={!canSubmit}
        onClick={submit}
        className="self-start"
      >
        {t("pool.settings.credSubmitButton")}
      </Button>
    </div>
  );
}
